/**
 * Production-safe logging utility
 * Automatically disables detailed logging in production builds
 */

import { LogLevel, LogContext } from '@/app/types/utils/logging';

class Logger {
  private isDevelopment: boolean;
  private enabledLevels: Set<LogLevel>;

  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    
    // In production, only show warnings and errors
    this.enabledLevels = this.isDevelopment 
      ? new Set(['debug', 'info', 'warn', 'error'])
      : new Set(['warn', 'error']);
  }

  private shouldLog(level: LogLevel): boolean {
    return this.enabledLevels.has(level);
  }

  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;
    
    if (context) {
      const contextStr = Object.entries(context)
        .map(([key, value]) => `${key}=${value}`)
        .join(' ');
      return `${prefix} ${message} | ${contextStr}`;
    }
    
    return `${prefix} ${message}`;
  }

  // Helper method to normalize context parameter - handles any type safely
  private normalizeContext(context?: any): LogContext | undefined {
    if (context === undefined || context === null) {
      return undefined;
    }
    
    // If it's already a proper LogContext object, return as-is
    if (typeof context === 'object' && !Array.isArray(context) && context.constructor === Object) {
      return context as LogContext;
    }
    
    // If it's a primitive value, wrap it in a generic context
    if (typeof context === 'string' || typeof context === 'number' || typeof context === 'boolean') {
      return { value: context };
    }
    
    // If it's an array, convert to object
    if (Array.isArray(context)) {
      return { items: context };
    }
    
    // For any other type, try to stringify it safely
    try {
      return { data: JSON.stringify(context) };
    } catch {
      return { data: String(context) };
    }
  }

  debug(message: string, context?: any): void {
    if (this.shouldLog('debug')) {
      console.log(this.formatMessage('debug', message, this.normalizeContext(context)));
    }
  }

  info(message: string, context?: any): void {
    if (this.shouldLog('info')) {
      console.info(this.formatMessage('info', message, this.normalizeContext(context)));
    }
  }

  warn(message: string, context?: any): void {
    if (this.shouldLog('warn')) {
      console.warn(this.formatMessage('warn', message, this.normalizeContext(context)));
    }
  }

  error(message: string, error?: any, context?: any): void {
    if (this.shouldLog('error')) {
      console.error(this.formatMessage('error', message, this.normalizeContext(context)));
      if (error) {
        console.error(error);
      }
    }
  }

  // Specialized methods for common use cases
  apiCall(method: string, url: string, context?: any): void {
    this.debug(`API ${method} ${url}`, context);
  }

  apiResponse(method: string, url: string, status: number, context?: any): void {
    this.debug(`API ${method} ${url} -> ${status}`, context);
  }

  userAction(action: string, userId?: number, context?: any): void {
    this.info(`User action: ${action}`, { userId, ...this.normalizeContext(context) });
  }

  preferenceLoad(type: string, userId: number, success: boolean, context?: any): void {
    const level = success ? 'info' : 'warn';
    const message = `${type} preference ${success ? 'loaded' : 'failed'} for user ${userId}`;
    
    if (level === 'info') {
      this.info(message, context);
    } else {
      this.warn(message, context);
    }
  }

  // Authentication and account management
  auth(message: string, context?: any): void {
    this.info(`Auth: ${message}`, context);
  }

  accountSwitch(fromUserId: number | null, toUserId: number, context?: any): void {
    this.info(`Account switch: ${fromUserId} -> ${toUserId}`, context);
  }

  // Theme and preferences
  theme(message: string, context?: any): void {
    this.debug(`Theme: ${message}`, context);
  }

  // Simple Notes specific
  simpleNotes(message: string, context?: any): void {
    this.debug(`SimpleNotes: ${message}`, context);
  }

  // Multi-account operations
  multiAccount(message: string, context?: any): void {
    this.debug(`MultiAccount: ${message}`, context);
  }

  // Redux thunks and state management
  redux(message: string, context?: any): void {
    this.debug(`Redux: ${message}`, context);
  }

  // Service operations
  service(serviceName: string, message: string, context?: any): void {
    this.debug(`${serviceName}: ${message}`, context);
  }

  // Performance and timing
  performance(message: string, timing?: number, context?: any): void {
    const perfContext = timing ? { ...this.normalizeContext(context), timing: `${timing}ms` } : this.normalizeContext(context);
    this.debug(`Performance: ${message}`, perfContext);
  }

  // Cleanup and lifecycle
  cleanup(message: string, context?: any): void {
    this.debug(`Cleanup: ${message}`, context);
  }
}

// Export singleton instance
export const logger = new Logger();

// Export types for external use
export type { LogLevel, LogContext };